"use client";
/**
 * Discovery deck — warm-archive aesthetic, framer-motion drag gestures.
 *
 * Card face: PUBLIC-class stats only (alias, height, eye/hair color, heritage,
 * education, proven-donor badge, program badge). SENSITIVE_PHI / GENETIC
 * summaries appear only on the detail view, post-consent checks, wrapped in
 * <Disclaimer section="GENETIC" /> (rules A-2, G-1..G-5).
 *
 * Gestures: drag right = Interested (LIKE), left = Not now (PASS),
 * up = Top choice (SUPERLIKE). Buttons mirror every gesture. Arrow keys and
 * the on-screen arrows BROWSE without recording anything (2026-07 partner
 * feedback: looking must never be mistaken for deciding); only an explicit
 * button press or drag writes a swipe. ArrowUp remains Top choice.
 *
 * Portraits: cards carry no media refs (photos are BIOMETRIC_MEDIA, not
 * PUBLIC); the deck renders deterministic duotone monogram art from the id.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { CardFace, type SwipeCard } from "./CardFace";

export type { SwipeCard } from "./CardFace";

export type SwipeDirection = "LIKE" | "PASS" | "SUPERLIKE";

const DRAG_COMMIT_PX = 120;
const FLICK_COMMIT_VELOCITY = 800;

function TopCard({
  card,
  onDecide,
}: {
  card: SwipeCard;
  onDecide: (direction: SwipeDirection) => void;
}) {
  const t = useTranslations("deck");
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-260, 260], [-11, 11]);
  const likeOpacity = useTransform(x, [48, 150], [0, 1]);
  const passOpacity = useTransform(x, [-150, -48], [1, 0]);
  const starOpacity = useTransform(y, [-150, -48], [1, 0]);

  return (
    <motion.article
      className="swipe-card swipe-card-top"
      style={{ x, y, rotate }}
      drag
      dragElastic={0.68}
      dragSnapToOrigin
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      whileDrag={{ scale: 1.02 }}
      onDragEnd={(_, info) => {
        const { offset, velocity } = info;
        if (offset.y < -DRAG_COMMIT_PX && Math.abs(offset.x) < DRAG_COMMIT_PX) {
          onDecide("SUPERLIKE");
        } else if (offset.x > DRAG_COMMIT_PX || velocity.x > FLICK_COMMIT_VELOCITY) {
          onDecide("LIKE");
        } else if (offset.x < -DRAG_COMMIT_PX || velocity.x < -FLICK_COMMIT_VELOCITY) {
          onDecide("PASS");
        }
      }}
    >
      <CardFace card={card} />
      <motion.span className="stamp stamp-like" style={{ opacity: likeOpacity }}>
        {t("interested")}
      </motion.span>
      <motion.span className="stamp stamp-pass" style={{ opacity: passOpacity }}>
        {t("notNow")}
      </motion.span>
      <motion.span className="stamp stamp-star" style={{ opacity: starOpacity }}>
        {t("topChoice")}
      </motion.span>
    </motion.article>
  );
}

const EXIT_TARGETS: Record<SwipeDirection | "BROWSE", { x: number; y: number; rotate: number }> = {
  LIKE: { x: 560, y: -40, rotate: 14 },
  PASS: { x: -560, y: -40, rotate: -14 },
  SUPERLIKE: { x: 0, y: -720, rotate: -3 },
  BROWSE: { x: 0, y: 0, rotate: 0 }, // browsing fades in place — no directional "verdict" animation
};

export function SwipeDeck({
  cards,
  onSwipe,
}: {
  cards: SwipeCard[];
  onSwipe: (id: string, direction: SwipeDirection) => Promise<void>;
}) {
  // Decided cards leave the browsable set; `pos` browses freely within what's
  // left (arrows/keys), while decide() records and removes.
  const t = useTranslations("deck");
  const [decidedIds, setDecidedIds] = useState<ReadonlySet<string>>(new Set());
  const [pos, setPos] = useState(0);
  const lastExit = useRef<SwipeDirection | "BROWSE">("PASS");
  const reducedMotion = useReducedMotion();
  const deckRef = useRef<HTMLElement>(null);

  const remaining = cards.filter((c) => !decidedIds.has(c.id));
  const current = remaining[pos];

  const decide = useCallback(
    (direction: SwipeDirection) => {
      const card = remaining[pos];
      if (!card) return;
      lastExit.current = direction;
      setDecidedIds((ids) => new Set([...ids, card.id]));
      // Removal shifts the list left, so `pos` now points at the next card;
      // clamp for the end of the deck.
      setPos((p) => Math.min(p, Math.max(remaining.length - 2, 0)));
      // Optimistic advance; the swipe write is PII (see types) — on failure we
      // log nothing client-side (L-2/H-7c); the server owns audit/retry.
      onSwipe(card.id, direction).catch(() => {});
    },
    [remaining, pos, onSwipe],
  );

  const browse = useCallback(
    (delta: -1 | 1) => {
      lastExit.current = "BROWSE";
      setPos((p) => Math.min(Math.max(p + delta, 0), Math.max(remaining.length - 1, 0)));
    },
    [remaining.length],
  );

  useEffect(() => {
    const node = deckRef.current;
    if (!node) return;
    const onKey = (e: KeyboardEvent) => {
      // Left/right browse only — no swipe is recorded from arrow keys.
      if (e.key === "ArrowRight") { e.preventDefault(); browse(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); browse(-1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); decide("SUPERLIKE"); }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [decide, browse]);

  if (!current) {
    return (
      <section className="deck deck-empty" aria-label={t("deckAria")}>
        <div className="deck-empty-card">
          <h2>{t("emptyTitle")}</h2>
          <p>{t("emptyBody")}</p>
        </div>
      </section>
    );
  }

  const upcoming = remaining.slice(pos + 1, pos + 3);

  return (
    <section
      className="deck"
      aria-label={t("deckAria")}
      ref={deckRef}
      tabIndex={0}
    >
      <p className="visually-hidden" aria-live="polite">
        {t("srStatus", { alias: current.alias, current: pos + 1, total: remaining.length })}
      </p>

      <div className="deck-stack">
        {upcoming
          .map((card, depth) => (
            <div
              key={card.id}
              className="swipe-card swipe-card-under"
              style={{
                transform: `translateY(${(depth + 1) * 12}px) scale(${1 - (depth + 1) * 0.04})`,
                zIndex: upcoming.length - depth,
              }}
              aria-hidden="true"
            >
              <CardFace card={card} />
            </div>
          ))
          .reverse()}

        <AnimatePresence mode="popLayout">
          <motion.div
            key={current.id}
            className="deck-top-slot"
            initial={reducedMotion ? { opacity: 0 } : { y: 14, scale: 0.96, opacity: 0 }}
            animate={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            exit={
              reducedMotion || lastExit.current === "BROWSE"
                ? { opacity: 0, transition: { duration: 0.15 } }
                : {
                    ...EXIT_TARGETS[lastExit.current],
                    opacity: 0,
                    transition: { duration: 0.34, ease: [0.32, 0.72, 0.35, 1] },
                  }
            }
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
          >
            <TopCard card={current} onDecide={decide} />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="deck-actions" role="group" aria-label={t("respondAria")}>
        <button
          type="button"
          className="action action-browse"
          onClick={() => browse(-1)}
          disabled={pos === 0}
          aria-label={t("prevAria")}
          title={t("prevTitle")}
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" className="action action-pass" onClick={() => decide("PASS")}>
          {t("notNow")}
        </button>
        <button
          type="button"
          className="action action-star"
          onClick={() => decide("SUPERLIKE")}
          aria-label={t("topChoice")}
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2.75c.7 3.4 2.85 5.55 6.25 6.25-3.4.7-5.55 2.85-6.25 6.25-.7-3.4-2.85-5.55-6.25-6.25 3.4-.7 5.55-2.85 6.25-6.25Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" className="action action-like" onClick={() => decide("LIKE")}>
          {t("interested")}
        </button>
        <button
          type="button"
          className="action action-browse"
          onClick={() => browse(1)}
          disabled={pos >= remaining.length - 1}
          aria-label={t("nextAria")}
          title={t("nextTitle")}
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 4.5 13 10l-5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      <p className="deck-count" aria-hidden="true">
        {t("count", { current: pos + 1, total: remaining.length })}
      </p>
    </section>
  );
}
